import { autoinject } from "aurelia-dependency-injection";
import { Reflection } from "../../helpers/reflection";
import { computedFrom } from "aurelia-framework";
import { UserClientService } from "../../services/basic/user-client.service";
import type { ExtendedRouteConfig, MenuTab, NavigationSection } from "../../models/basic.models";
import * as Enumerable from "linq";
import { UserClaimsService } from "../../services/basic/user-claims.service";
import { type RouteConfig, Router } from "aurelia-router";
import { ConfigService } from "../../services/basic/config.service";
import { AuthenticationService } from "../../services/basic/authentication.service";
import { Claims } from "../../constants/authentication/claims";
import { BaseClaims } from "../../constants/authentication/base-claims";
import { RoutesService } from "../../services/routes/routes.service";
import { Messages } from "../../constants/event-messages.constants";
import { EventAggregator } from "aurelia-event-aggregator";
import { ClaimsRequireTypeEnum } from "../../models/user.models";
import { CustomerService } from "../../services/basic/customer-client.service";
import { RightStoreService } from "../../services/rightstore/rightstore.service";
import { SubscriptionProductCodes, SubscriptionTypeEnum } from "../../models/rightstore/rightstore.models";
import type { EntityTypeConfigurationModel } from "../../components/comp-entity-finder/comp-entity-finder.models";
import { BindingSignaler } from "aurelia-templating-resources";
import { EntityTypeConfigurationHelper } from "../../helpers/entity-type-configuration.helper";
import { GuidHelper } from "../../helpers/guid.helper";
import { DataType, QueryFilter } from "../../models/queries.models";
import { CacheClientService, CacheLocation } from "../../services/basic/client-caching.service";
import { RouteGenerator } from "../../route-generator";
import { SearchHistoryClientService } from "../../services/basic/search-history-client.service";
import { jam } from "@jam.dev/sdk";
import { PlatformFeatureKeys } from "../../dialogs/upgrade-from-freemium/freemium-feature-info-model";

//Load styles
require("./partial-layout.scss");

@autoinject
export class PartialLayout {

    private _betaMenusKey: string[] = ['Home'];
    private _documentClick: EventListenerOrEventListenerObject;
    public appClientVersion: string;
    public environment: string;
    public navigationSections: NavigationSection[] = [];
    public selectedRoute: RouteConfig;
    public collapseSidebar: boolean = false;
    public showGlobalSearch: boolean = false;
    public showSidebar: boolean = false;
    public hasMoreMenu: boolean = false;
    public settingsDropdownOpen: boolean;
    public searchFilter: string;
    public isMyVesselNavigationActive: boolean;
    public isMenuLoaded: boolean = false;

    //Permissions
    public isLimitedCustomerSubscription: boolean;
    public hasVersionHistoryPermission: boolean;
    public canAccessCompanies: boolean;
    public canAccessVessels: boolean;
    public showRightStoreLinkPermission: boolean;
    public isRightShipUser: boolean;
    public canViewSettings: boolean;
    public canViewMySubscription: boolean;

    constructor(private configService: ConfigService, private router: Router, private routeGenerator: RouteGenerator, private userClientService: UserClientService,
        private userClaimsService: UserClaimsService, private authenticatonService: AuthenticationService,
        private routesService: RoutesService, private eventAggregator: EventAggregator, private customerService: CustomerService,
        private rightStoreService: RightStoreService, private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        private cacheClientService: CacheClientService, private bindingSignaler: BindingSignaler, private searchHistoryService: SearchHistoryClientService) {
        this.appClientVersion = this.configService.config.version;
        this.environment = this.configService.config.environment;

        //On document click, close any open dropdowns
        this._documentClick = e => {
            if (!(<any>e).propagationStopped) {
                this.settingsDropdownOpen = false;
                eventAggregator.publish(Messages.documentClick, { source: "document" });
            }
        };
    }

    @computedFrom('configService.config.urls.myVesselsUrl')
    get myVesselsUrl() {
        return this.configService.config.urls.myVesselsUrl;
    }

    @computedFrom('this.userClientService.currentUser')
    get entityTypes(): EntityTypeConfigurationModel[] {
        return this.entityTypeConfigurationHelper.getConfigurationByKey(["InformativeVesselSearch", "InformativeCompanySearch"]);
    }

    @computedFrom('this.router.currentInstruction.config.name')
    get isMySubscriptionNavigationSelected(): boolean {
        return this.router.currentInstruction.config.name == "my-subscription";
    }

    @computedFrom('this.router.currentInstruction.config.name')
    get isMyAccountNavigationSelected(): boolean {
        return this.router.currentInstruction.config.name == "my-account";
    }

    @computedFrom('this.router.currentInstruction.config.name')
    get isSettingsNavigationSelected(): boolean {
        return this.router.currentInstruction.config.name == "settings";
    }

    async attached() {

        //User should land in the home page if they have access to it when visiting platform.rightship.com
        if (window.location.origin == this.configService.config.urls.rightShipPlatformUrl && 
            window.location.pathname == "/" && this.showRightFleetStarterLink) {
            window.location.href = this.myVesselsUrl;
        }

        //Event listener for window resize to hide/show sidebar
        window.addEventListener('resize', this.appSidebarMouseLeave.bind(this)); // Bind the function to the current instance of PartialLayout

        if(this.userClientService.currentUser) {

            jam.metadata(() => {
                return {
                    user: this.userClientService.currentUser.user.email
                }
            });
            
            const customerId = await this.customerService.getCustomerId();
            const subscriptionResponse = customerId > 0 ? await this.rightStoreService.getCustomerSubscription(customerId, SubscriptionProductCodes.informationServices) : null;
            this.isLimitedCustomerSubscription = subscriptionResponse?.customerSubscription?.subscriptionTypeId == SubscriptionTypeEnum.LimitedSubscription;

            //Set permissions
            this.canAccessCompanies = this.userClaimsService.hasPermission(Claims.CanAccessCompanies);
            this.canAccessVessels = this.userClaimsService.hasPermission(Claims.CanAccessVessels);
            this.hasVersionHistoryPermission = this.userClaimsService.hasPermission(BaseClaims.ViewVersions);
            this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
            this.canViewMySubscription = this.userClaimsService.hasPermission(Claims.CanAccessMySubscription) ||
                ((subscriptionResponse?.customerSubscription?.subscriptionTypeId == SubscriptionTypeEnum.FullSubscription ||
                    (subscriptionResponse?.customerSubscription?.subscriptionTypeId == SubscriptionTypeEnum.LimitedSubscription 
                        && subscriptionResponse?.customerSubscription?.maxVesselsCount > 0))
                    && !subscriptionResponse?.customerSubscription?.isExpired
                );
            this.canViewSettings = this.userClaimsService.hasPermissions([Claims.CanViewAllBusinessUnits, Claims.CanViewMyBusinessUnit,
                Claims.CanManageRoles, Claims.CanViewAllPeople, Claims.CanViewPeopleInMyBusinessUnit], ClaimsRequireTypeEnum.RequireAny)
            
            //Claims
            if (this.userClaimsService.hasPermission(Claims.ShowInsightsBetaAndFreeTrialBanner)) {
                if (this._betaMenusKey.indexOf('RightshipInsights') == -1) {
                    this._betaMenusKey.push('RightshipInsights');
                }
            }
            this.showRightStoreLinkPermission = this.userClaimsService.hasPermission(Claims.ShowRightStoreLink);

            //Check if user is on My Vessels site
            this.isMyVesselNavigationActive = window.location.href.includes(this.myVesselsUrl);

            this.navigationSections = await this.routesService.getAppMenu();
        
            const canAccessListAlerts = this.userClaimsService.hasPermission(Claims.CanAccessListAlerts);

            if (!canAccessListAlerts) {
                let menuItems;

                this.navigationSections.forEach(navigationSection => {
                    menuItems = navigationSection.menuTabs.filter(x => x.routes && x.routes.filter(y => y.title == "List & Alerts").length > 0);
                });
                    
                const menuItem = menuItems.length > 0 ? menuItems[0] : null;
                if (menuItem && menuItem.routes && menuItem.routes[0]) {
                    menuItem.routes[0].title = "Lists";
                }
            }
        }

        //Set selected route
        this.selectedRoute = Enumerable.from(this.routeGenerator.getRoutes()).firstOrDefault(x => x.name == this.router?.currentInstruction?.config?.name);
        this.isMenuLoaded = true;

        //Subscribe to document click event
        document.addEventListener("click", this._documentClick);
    }

    detached() {
        if (document) {

            //Unsubscribe from document click event
            document.removeEventListener("click", this._documentClick);
        }
    }

    @computedFrom(Reflection.getPropertyPath<PartialLayout>(z => z.userClientService.currentUser))
    get isAuthenticated() {

        //RightShipQiRouterConstants.RouteNames.TermsAndConditions + added resetpassword for bug DEL-15936
        return this.userClientService.currentUser != null && window.location.href.indexOf("terms-and-conditions") == -1 && 
            window.location.href.indexOf("resetpassword") == -1;
    }

    //Shows the "Home" navigation item based of the customer type and permission
    @computedFrom(Reflection.getPropertyPath<PartialLayout>(z => z.userClientService.currentUser))
    get showRightFleetStarterLink() {

        //Ship Owner and Ship Manager customer type are able to access the My Vessels portal DEL-15937
        const customerTypes = ["ship owner", "ship manager"];
        const currentUserCustomerType = this.userClientService.currentUser?.businessUnit?.customerType;

        return customerTypes.includes(currentUserCustomerType?.toLowerCase()) || this.userClaimsService.hasPermission(Claims.ShowRightFleetStarterLink);
    }

    //Checks if the current section is the "Products" section
    public isProductsSection(section: NavigationSection) {
        return section.title == "Products";
    }

    //Navigate back to previous page - Used in the header for mobile view
    public navigateBack() {
        this.router.navigateBack();
    }

    //Toggle global seach on mobile
    public toggleGlobalSearch() {
        this.showGlobalSearch = !this.showGlobalSearch;
    }

    getRouteName(menuTab: MenuTab) {
        const routes = menuTab.routes.filter(x => !(x.isVisible == false));

        if (routes.length > 1) {
            return menuTab.title;
        }

        const route = routes[0];
        return route.title;
    }

    /**
     * Navigate to route (this method is triggered when a menu/sub-menu item is clicked)
     * @param event Browser event
     * @param routes The clicked menu item's routes
     * @param menuTab The clicked menu item
     * @param isFromMain If 'false', a sub-menu item was clicked. Else true
     * @param menuTabIndex If 'isFromMain' is true, the menu item index. Else -1
     */
    navigate(event, routes: RouteConfig[], menuTab: MenuTab, isFromMain: boolean = false, sectionIndex: number = -1, menuTabIndex: number = -1) {

        //If the current customer has a freemium account show dialog instead of navigating to page
        if (menuTab.lockedFeature) {
            this.eventAggregator.publish(Messages.unlockFeature, menuTab.key.toLowerCase());
        }
        else {

            //If the current menu tab has more than one sub-routes
            if (routes.length > 1) {

                //If the current menu tab is opened
                if (menuTab.showDropdownMenu) {

                    //Hide current menu tab
                    this.navigationSections[sectionIndex].menuTabs[menuTabIndex].showDropdownMenu = false;
                }
                else {
                    //Close all currently opened dropdown
                    this.navigationSections[sectionIndex].menuTabs.forEach(x => x.showDropdownMenu = false);

                    //Show current tab
                    this.navigationSections[sectionIndex].menuTabs[menuTabIndex].showDropdownMenu = true;

                    //Open sidebar
                    this.collapseSidebar = false;
                }

                return;
            }

            if (isFromMain) {
                //hide all
                this.navigationSections[sectionIndex].menuTabs.forEach(x => {
                    x.showDropdownMenu = false;
                });
            }
            this.showSidebar = false;
            event.stopPropagation();

            //If the current menu tab has only one sub-route
            const routeLink = this.getRouteLink(menuTab);
            if (routeLink) {
                const link = routeLink.replace(window.location.origin, '');
                if(link.startsWith("/")) {
                    this.router.navigate(link);
                }
                else {

                    //open in new tab if the link is external
                    if (this.openInNewTab(menuTab.key)) {
                        window.open(link, '_blank');
                        return;
                    }
                    window.location.href = link;
                }
            }
            else if (routes.length > 0){
                const route = Array.isArray(routes[0].route)? routes[0].route[0] : routes[0].route;
                this.router.navigate(route);
            }

            this.selectedRoute = this.router.routes.filter(x => x.route == routes[0].route)[0];
            this.bindingSignaler.signal('refresh-is-active');
        }
    }

    navigateSubRoute(subRoute: ExtendedRouteConfig) {
        const routeLink = this.getSubRouteLink(subRoute);
        if (routeLink) {
            const link = routeLink.replace(window.location.origin, '');
            if(link.startsWith("/"))
                this.router.navigate(link);
            else
                window.location.href = link;
        }
    }

    isMenuBeta(menuTab) {
        return this._betaMenusKey?.some(x => x == menuTab);
    }

    getRouteLink(menuTab: MenuTab) {

        //If the current customer has a freemium account OR menuTab has dropdown don't navigating
        if (menuTab.lockedFeature || this.getMenuRoutes(menuTab).length > 1)
            return
        if (menuTab.key == "Home") {
            return this.myVesselsUrl;
        }
        if (menuTab.key == "CrewWelfare") {
            return this.configService.config.urls.crewWelfareUrl;
        }
        if (menuTab.key == "PscRiskiq") {
            return this.configService.config.urls.pscRiskIqUrl;
        }
        return this.configService.config.urls.rightShipPlatformUrl + "/" + menuTab.routes[0].route;
    }

    getSubRouteLink(subRoute: ExtendedRouteConfig) {
        return this.configService.config.urls.rightShipPlatformUrl + "/" + subRoute.route;
    }

    //If a menu item has its sub-items displayed, but is not active, close it
    closeInactiveMenuItems() {
        this.navigationSections.forEach(x => x.menuTabs.forEach((menuItem: MenuTab) => {
            if (menuItem.showDropdownMenu && !this.isMainMenuItemActive(menuItem)) {
                menuItem.showDropdownMenu = false;
            }
        }));
    }

    //Toggle sidebar on mouse enter
    appSidebarMouseEnter() {
        if (window.innerWidth >= 1024 && window.innerWidth <= 1800) {
            this.collapseSidebar = false;
        }
    }

    //Toggle sidebar on mouse leave
    appSidebarMouseLeave() {
        if (window.innerWidth >= 1024 && window.innerWidth <= 1800) {
            this.collapseSidebar = true;
            this.closeInactiveMenuItems();
        }
    }

    //Show the menu tab if it has at least one visible route
    showMenuTab(menuTab: MenuTab, isMore: boolean = false): boolean {
        let claims = [];

        const routes = menuTab.routes.filter(x => !(x.isVisible == false));
        return routes.filter(route => {

            if (route && typeof route.claimsRequired == "function") {
                claims = route.claimsRequired();
            } else if (route && typeof route.claimsRequired != "function") {
                claims = route.claimsRequired;
            }

            const isVisible = route && this.userClaimsService.hasPermissions(claims, route.claimsRequireType);

            if (isMore) {
                this.hasMoreMenu = this.hasMoreMenu || isVisible;
            }

            return isVisible;

        }).length > 0;
    }

    //Check if a menu item (a main menu item, not a sub-menu item) is currently selected
    isMainMenuItemActive(mainMenuTab: MenuTab) {
        let menuRoutes = this.getMenuRoutes(mainMenuTab, true);
        const routesWithChildRoutes = Enumerable.from(menuRoutes).where(x => x.children && x.children.length > 0);
        menuRoutes = menuRoutes.concat(routesWithChildRoutes.selectMany(x => x.children).toArray());

        if (mainMenuTab && this.selectedRoute) {

            //If the selected route belongs to a Port
            if ((this.selectedRoute.route as string).includes(':portId')) {
                if (mainMenuTab.key === `port-${location.pathname.substr(location.pathname.length - 1, 1)}`) {

                    //Show mainMenuTab sub navigation
                    // this.navigationSections.forEach(x=> x.menuTabs[this.menu.indexOf(mainMenuTab)].showDropdownMenu = true;
                    return true;
                }
                return false;
            }

            if (mainMenuTab.key === "Home") {
                return window.location.origin == this.myVesselsUrl;
            }

            if (mainMenuTab.key === "PscRiskiq") {
                return window.location.origin == this.configService.config.urls.pscRiskIqUrl;
            }

            //If the main menu tab has at least one sub navigation route currently active
            return menuRoutes.some(x => x.name == this.selectedRoute.name);// || menuRoutes.some(x => this.activeMenuItemsService.isActive(x));
        } 
        else {
            return false;
        }
    }

    getMenuRoutes(menuTab: MenuTab, hasHiddenRoutes: boolean = false) {
        const routes: ExtendedRouteConfig[] = [];
        if (menuTab) {
            menuTab.routes.forEach(route => {
                route.isVisible = route.isVisible != false ? this.hasClaim(route) : route.isVisible;
                routes.push(route);
                if (route.createNewRoute) {
                    route.createNewRoute.isVisible = route.createNewRoute.isVisible != false ? this.hasClaim(route.createNewRoute) : route.createNewRoute.isVisible;
                    routes.push(route.createNewRoute);
                }
                if (route.editRoute && typeof (route.editRoute) != "string") {
                    route.editRoute.isVisible = route.editRoute.isVisible != false ? this.hasClaim(route.editRoute) : false;
                    routes.push(route.editRoute);
                }
            });
        }
        const visibleRoutes = routes.filter(x => x.isVisible || hasHiddenRoutes);
        if (visibleRoutes.length == 1 && visibleRoutes[0].children && visibleRoutes[0].children.length > 0) {
            return routes.filter(x => x.moduleId != null).concat(this.getChildrenRoutes(visibleRoutes[0]));
        }
        return routes.filter(a => a.isVisible == null || a.isVisible || hasHiddenRoutes);
    }

    onLogOut(): void {
        this.authenticatonService.logOut();
    }

    hasClaim(route: ExtendedRouteConfig): boolean {
        if (!(this.userClientService.currentUser && route))
            return false;

        let claims = [];
        if (route && typeof route.claimsRequired == "function") {
            claims = route.claimsRequired();
        } else if (route && typeof route.claimsRequired != "function") {
            claims = route.claimsRequired;
        }

        return this.userClaimsService.hasPermissions(
            route ? claims : null, route && route.claimsRequireType ? route.claimsRequireType : ClaimsRequireTypeEnum.RequireAll
        );
    }

    getChildrenRoutes(route: ExtendedRouteConfig): ExtendedRouteConfig[] {
        const routes: ExtendedRouteConfig[] = [];
        route.children.forEach(r => {
            if (!this.userClaimsService.hasPermissions(r.claimsRequired, r.claimsRequireType)) {
                return;
            }
            routes.push(r);
            if (r.createNewRoute && this.userClaimsService.hasPermissions(r.createNewRoute.claimsRequired, r.createNewRoute.claimsRequireType)) {
                routes.push(r.createNewRoute);
            }
            if (r.editRoute && typeof (route.editRoute) != "string" && this.userClaimsService.hasPermissions(r.editRoute.claimsRequired, r.editRoute.claimsRequireType)) {
                routes.push(r.editRoute);
            }
        });
        return routes;
    }

    //Toggle the sidebar menu
    toggleCollapsableSidebar() {
        this.collapseSidebar = !this.collapseSidebar;

        if (this.collapseSidebar) {
            this.closeInactiveMenuItems();
        }
    }

    //Toggle the sidebar menu
    public toggleShowSidebar() {
        this.showSidebar = !this.showSidebar;
        this.closeInactiveMenuItems();
    }

    //View profile
    viewProfile() {
        this.router.navigate(this.configService.config.urls.rightShipPlatformUrl + "/my-account");
        this.toggleShowSidebar();
    }

    //View my subscription
    viewMySubscription() {
        this.router.navigate(this.configService.config.urls.rightShipPlatformUrl + "/my-subscription");
    }

    //View settings
    viewSettings() {
        this.router.navigate(this.configService.config.urls.rightShipPlatformUrl + "/settings");
    }

    //View version history
    versionHistoryUrl() {
        const path = "/admin/versions";
        if (window.location.href.includes(this.myVesselsUrl)) {
            return this.configService.config.urls.rightShipPlatformUrl + path;
        }
        else {
            return path
        }
    }

    //Open settings dropdown
    public openSettingsDropdown(event) {
        event.stopPropagation();
        this.settingsDropdownOpen = !this.settingsDropdownOpen;
    }

    //Returns the user's initials to display in the profile
    public getUserInitials() {
        let initials = "";
        if (this.userClientService.currentUser && this.userClientService.currentUser.user) {
            const fullName = this.userClientService.currentUser.user.fullName.split(' ');
            initials = fullName.shift().charAt(0) + fullName.pop().charAt(0);
        }
        return initials.toUpperCase();
    }

    //Log the user out
    public logOut(): void {
        this.authenticatonService.logOut();
    }

    @computedFrom('configService.config.urls.rightStoreUrl')
    get rightstoreUrl() {

        //This is done because in appsettings only appsettings.production we don't pass the ?env=production query string
        let concatination = "";
        if (this.environment.toLowerCase() != "production") {
            concatination = "env=" + this.environment + "&";
        }
        return this.configService.config.urls.rightStoreUrl + "/Catalog/products?" + concatination + "utm_source=platform&utm_medium=layout&utm_campaign=rightstore";
    }

    //DEL-14026 - This is to hide the nvagation, banners and search for specifically the RFQ of RS Inspection
    get hideLayoutElements(): boolean {
        return window.location.href.includes('/rfq/rfq-add-rightship-inspection');
    }

    public openSearchHistory() {
        this.router.navigate(this.configService.config.urls.rightShipPlatformUrl + "/search-history");
    }

    public openAdvancedSearch() {
        if (this.isLimitedCustomerSubscription){
            return this.eventAggregator.publish(Messages.unlockFeature, PlatformFeatureKeys.advancedSearch);
        }

        let url = this.configService.config.urls.rightShipPlatformUrl + "/advanced-search";
        if (this.searchFilter) {
            url += `?keywords=${encodeURIComponent(this.searchFilter)}`;
        }
        this.router.navigate(url);
    }

    async searchItemViewMore(event) {
        const detail = event.detail;
        const isVessel = detail.uri == "VesselSearch";
        const isCompany = detail.uri == "CompanySearch";
        const filters = this.generateFilters(isVessel, isCompany, detail.filter);

        const routeName = isVessel ? "vessels-search" : isCompany ? "companies-search" : null;
        if (!routeName) return;

        //Caching
        await this.cacheClientService.put(`search-keywords-${routeName}`, detail.filter, CacheLocation.LocalStorage);
        await this.cacheClientService.put(`search-filters-${routeName}`, filters, CacheLocation.LocalStorage);
        
        //Navigate to search
        const type = isVessel ? "vessels":"companies"
        const url = this.configService.config.urls.rightShipPlatformUrl + "/search/"+ type +"?t=" + new Date().getTime();
        this.router.navigate(url);
    }

    generateFilters(isVessel: boolean, isCompany: boolean, keywords: string) {
        const filters = [];
        const guid = GuidHelper.generateGuid();
        if (isVessel) {
            filters.push(QueryFilter.getQuery("name", keywords, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            filters.push(QueryFilter.getQuery("internationalMaritimeOrganizationNumber", keywords, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            filters.push(QueryFilter.getQuery("previousName", null, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            filters.push(QueryFilter.getQuery("isDeadVessel", false, "eq", DataType.Boolean, true));
        } else if (isCompany) {
            filters.push(QueryFilter.getQuery("fullName", keywords, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
        }

        return filters;
    }

    //Search item selected
    searchItemSelected(e: any) {
        const detail = e.detail;
        if (detail && detail.selectedEntityUri && detail.selectedEntity) {
            this.searchHistoryService.saveSearchHistoryItem(e.detail);
        }
    }

    //Global search footer template
    globalSearchFooterTemplate() {
        const html =
            `<div class="footer">
                <h1 class="footer-title">Can't find what you're looking for?</h1>
                <p class="footer-text">
                    Try our <span class="link" click.trigger="advancedSearch()">advanced search</span> for all companies and all vessels, including the ones that are not in service.
                </p>
            </div>`;
        return html;
    }

    //Global search footer binding context
    globalSearchFooterBindingContext() {
        return {
            advancedSearch: () => this.openAdvancedSearch()
        };
    }

    //Toggle navigation section expander
    public toggleNavigationSectionExpander(navigationSection: NavigationSection) {

        //If the current section is the "Products" section, don't toggle
        if(this.isProductsSection(navigationSection)) return;

        //Toggle the section
        navigationSection.isOpen = !navigationSection.isOpen;
    }

    //Open in new tab
    public openInNewTab(menuTabKey){
        return menuTabKey == "CrewWelfare";
    }
}
